// @figma file=pkCGby54zXzTsccXvHqzO6 node=tbd name="FormSection" set="mytaible-shadcn-studio" verified=tbd

import * as React from 'react';
import { cn } from '../../../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../atoms/card';
import { Skeleton } from '../../atoms/skeleton';
import type { FormLayoutActionConfig } from './form-layout';
import { ActionButton, useFormLayoutDefaults } from './form-layout';

// ─── Types ────────────────────────────────────────────────────────────────────

export type FormSectionVariant = 'stacked' | 'side-label';
export type FormSectionDensity = 'compact' | 'default' | 'spacious';
export type FormSectionActionsPos = 'bottom' | 'header';

export interface FormSectionProps extends React.HTMLAttributes<HTMLElement> {
  /** Section heading rendered as h3 */
  title?: string;
  /** Subtitle rendered below the title as muted paragraph */
  description?: string;
  /**
   * When set, prefixes the title with the number (e.g. "1. Personal Info").
   * Intended for the numbered-section layout pattern.
   */
  number?: number;
  /**
   * Wraps the section in a Card with CardHeader + CardContent.
   * Use for the card-stack layout pattern.
   */
  asCard?: boolean;
  /**
   * Number of columns in the inner content grid.
   * Collapses to 1 column below the `sm` breakpoint (640 px).
   */
  columns?: 1 | 2 | 3;
  /** Optional action rendered in the top-right (stacked) or below description (side-label) */
  headerAction?: React.ReactNode;
  /**
   * Controls how the section header relates to its fields.
   * - `stacked` (default): title/description above the field grid
   * - `side-label`: title/description in a left column, fields in a right column
   */
  variant?: FormSectionVariant;
  /**
   * Controls field spacing inside the section.
   * - `compact`: tight gaps — suited for modals and sidebars
   * - `default`: standard gaps
   * - `spacious`: generous gaps — suited for onboarding and settings pages
   */
  density?: FormSectionDensity;
  /**
   * Inline cancel button rendered in the section footer.
   * Defaults: label="Cancel", variant="secondary", appearance="outline".
   * Use for the per-section independent-save pattern (Stripe-style settings).
   */
  cancelAction?: FormLayoutActionConfig;
  /**
   * Inline save button rendered in the section footer.
   * Defaults: label="Save", variant="primary", appearance="solid".
   * Use for the per-section independent-save pattern (Stripe-style settings).
   */
  submitAction?: FormLayoutActionConfig;
  /**
   * Raw escape hatch for fully custom section footer content.
   * When set, renders instead of cancelAction / submitAction.
   */
  actions?: React.ReactNode;
  /**
   * Where to render the inline cancel/submit buttons.
   * - `bottom` (default) — below the field grid, in a section footer row
   * - `header` — inside the section header, right-aligned alongside the title
   */
  actionsPosition?: FormSectionActionsPos;
  /**
   * When true, renders skeleton placeholders instead of children.
   * The skeleton matches the section's column count.
   * Drive with a query's `isLoading` flag.
   */
  loading?: boolean;
  /**
   * When true, shows a small amber dot next to the section title to signal
   * unsaved changes. Typically driven by react-hook-form's `formState.isDirty`
   * on a per-section form instance.
   */
  isDirty?: boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fieldGridClass: Record<FormSectionDensity, Record<1 | 2 | 3, string>> = {
  compact: {
    1: 'grid grid-cols-1 gap-3',
    2: 'grid grid-cols-1 sm:grid-cols-2 gap-3',
    3: 'grid grid-cols-1 sm:grid-cols-3 gap-3',
  },
  default: {
    1: 'grid grid-cols-1 gap-4',
    2: 'grid grid-cols-1 sm:grid-cols-2 gap-6',
    3: 'grid grid-cols-1 sm:grid-cols-3 gap-6',
  },
  spacious: {
    1: 'grid grid-cols-1 gap-6',
    2: 'grid grid-cols-1 sm:grid-cols-2 gap-8',
    3: 'grid grid-cols-1 sm:grid-cols-3 gap-8',
  },
};

const outerSpacingClass: Record<FormSectionDensity, string> = {
  compact: 'space-y-2',
  default: 'space-y-4',
  spacious: 'space-y-6',
};

// ─── DirtyDot ─────────────────────────────────────────────────────────────────

function DirtyDot() {
  return (
    <span
      role="img"
      aria-label="unsaved changes"
      title="Unsaved changes"
      className="ml-1.5 inline-block h-1.5 w-1.5 translate-y-[-1px] rounded-full bg-warning align-middle"
    />
  );
}

// ─── SectionSkeleton ──────────────────────────────────────────────────────────

function SectionSkeleton({
  columns,
  density,
  hasTitle,
  hasDescription,
  asCard,
  className,
}: {
  columns: 1 | 2 | 3;
  density: FormSectionDensity;
  hasTitle: boolean;
  hasDescription: boolean;
  asCard: boolean;
  className?: string;
}) {
  const gridCls = fieldGridClass[density][columns];
  const fieldCount = columns * 2;

  const body = (
    <>
      {(hasTitle || hasDescription) && (
        <div className="space-y-1.5">
          {hasTitle && <Skeleton className="h-4 w-36" />}
          {hasDescription && <Skeleton className="h-3 w-52" />}
        </div>
      )}
      <div className={gridCls}>
        {Array.from({ length: fieldCount }).map((_, i) => (
          <div key={i} className="flex flex-col gap-1.5">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-9 w-full" />
          </div>
        ))}
      </div>
    </>
  );

  if (asCard) {
    return (
      <Card className={cn('w-full', className)}>
        <CardContent className="space-y-4 pt-4 sm:pt-6">{body}</CardContent>
      </Card>
    );
  }

  return <div className={cn('w-full', outerSpacingClass[density], className)}>{body}</div>;
}

// ─── SectionFooter ────────────────────────────────────────────────────────────

function SectionFooter({
  actions,
  cancelAction,
  submitAction,
  inCard = false,
}: {
  actions?: React.ReactNode;
  cancelAction?: FormLayoutActionConfig;
  submitAction?: FormLayoutActionConfig;
  inCard?: boolean;
}) {
  const hasStructured = cancelAction !== undefined || submitAction !== undefined;
  const cancelVisible = cancelAction && cancelAction.show !== false;
  const submitVisible = submitAction && submitAction.show !== false;
  if (!(actions !== undefined || (hasStructured && (cancelVisible || submitVisible)))) return null;

  return (
    <div className={cn('flex items-center justify-end gap-3', inCard ? 'px-6 py-4' : 'pt-4')}>
      {actions ?? (
        <>
          {cancelAction && (
            <ActionButton
              config={cancelAction}
              defaultLabel="Cancel"
              defaultVariant="secondary"
              defaultAppearance="outline"
              defaultType="button"
            />
          )}
          {submitAction && (
            <ActionButton
              config={submitAction}
              defaultLabel="Save"
              defaultVariant="primary"
              defaultAppearance="solid"
              defaultType="submit"
            />
          )}
        </>
      )}
    </div>
  );
}

// ─── FormSection ──────────────────────────────────────────────────────────────

const FormSection = React.forwardRef<HTMLElement, FormSectionProps>(
  (
    {
      title,
      description,
      number,
      asCard = false,
      columns = 1,
      headerAction,
      variant: variantProp,
      density = 'default',
      cancelAction,
      submitAction,
      actions,
      actionsPosition = 'bottom',
      loading = false,
      isDirty = false,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const layoutDefaults = useFormLayoutDefaults();
    const variant = variantProp ?? layoutDefaults.sectionVariant ?? 'stacked';
    const titleId = React.useId();
    const headingText = title && number != null ? `${number}. ${title}` : title;
    const gridCls = fieldGridClass[density][columns];

    if (loading) {
      return (
        <SectionSkeleton
          columns={columns}
          density={density}
          hasTitle={!!title}
          hasDescription={!!description}
          asCard={asCard}
          className={className}
        />
      );
    }

    // ── Header-positioned action buttons ──────────────────────────────────────
    const inlineInHeader = actionsPosition === 'header';
    const hasStructuredActions = cancelAction !== undefined || submitAction !== undefined;
    const cancelVisible = cancelAction && cancelAction.show !== false;
    const submitVisible = submitAction && submitAction.show !== false;
    const showActionsNode =
      actions !== undefined || (hasStructuredActions && (cancelVisible || submitVisible));

    const headerActionsNode: React.ReactNode =
      inlineInHeader && showActionsNode ? (
        <div className="flex shrink-0 items-center gap-2">
          {actions ?? (
            <>
              {cancelAction && (
                <ActionButton
                  config={cancelAction}
                  defaultLabel="Cancel"
                  defaultVariant="secondary"
                  defaultAppearance="outline"
                  defaultType="button"
                />
              )}
              {submitAction && (
                <ActionButton
                  config={submitAction}
                  defaultLabel="Save"
                  defaultVariant="primary"
                  defaultAppearance="solid"
                  defaultType="submit"
                />
              )}
            </>
          )}
        </div>
      ) : null;

    const effectiveHeaderAction: React.ReactNode = headerActionsNode ? (
      headerAction ? (
        <>
          {headerAction}
          {headerActionsNode}
        </>
      ) : (
        headerActionsNode
      )
    ) : (
      headerAction
    );

    const footerProps = {
      actions: inlineInHeader ? undefined : actions,
      cancelAction: inlineInHeader ? undefined : cancelAction,
      submitAction: inlineInHeader ? undefined : submitAction,
    };

    // ── side-label ─────────────────────────────────────────────────────────────

    if (variant === 'side-label') {
      const header =
        headingText || description || effectiveHeaderAction ? (
          <div className="flex flex-col gap-1.5">
            {headingText && (
              <h3 id={titleId} className="text-sm font-semibold text-foreground">
                {number != null && <span className="text-primary">{number}.&nbsp;</span>}
                {title}
                {isDirty && <DirtyDot />}
              </h3>
            )}
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
            {effectiveHeaderAction && <div className="mt-2 shrink-0">{effectiveHeaderAction}</div>}
          </div>
        ) : null;

      const rightCol = (
        <div className="space-y-4">
          <div className={gridCls}>{children}</div>
          <SectionFooter {...footerProps} inCard={false} />
        </div>
      );

      if (asCard) {
        return (
          <Card
            ref={ref as React.Ref<HTMLDivElement>}
            className={cn('w-full', className)}
            aria-labelledby={headingText ? titleId : undefined}
            {...(props as React.HTMLAttributes<HTMLDivElement>)}
          >
            <CardContent className="grid grid-cols-1 gap-6 pt-6 md:grid-cols-[1fr_2fr] md:gap-8">
              {header}
              <div className={gridCls}>{children}</div>
            </CardContent>
            <SectionFooter {...footerProps} inCard />
          </Card>
        );
      }

      return (
        <section
          ref={ref as React.Ref<HTMLElement>}
          className={cn('grid w-full grid-cols-1 gap-6 md:grid-cols-[1fr_2fr] md:gap-8', className)}
          aria-labelledby={headingText ? titleId : undefined}
          {...props}
        >
          {header ?? <div />}
          {rightCol}
        </section>
      );
    }

    // ── stacked (default) ──────────────────────────────────────────────────────

    if (asCard) {
      return (
        <Card
          ref={ref as React.Ref<HTMLDivElement>}
          className={cn('w-full', className)}
          aria-labelledby={headingText ? titleId : undefined}
          {...(props as React.HTMLAttributes<HTMLDivElement>)}
        >
          {headingText || description || effectiveHeaderAction ? (
            <CardHeader
              className={cn(
                effectiveHeaderAction && 'flex flex-row items-start justify-between gap-4',
              )}
            >
              <div className="flex flex-col gap-1.5">
                {headingText && (
                  <CardTitle id={titleId} className="text-base font-semibold">
                    {headingText}
                    {isDirty && <DirtyDot />}
                  </CardTitle>
                )}
                {description && <CardDescription>{description}</CardDescription>}
              </div>
              {effectiveHeaderAction && <div className="shrink-0">{effectiveHeaderAction}</div>}
            </CardHeader>
          ) : null}
          <CardContent className={cn(gridCls, 'pt-0')}>{children}</CardContent>
          <SectionFooter {...footerProps} inCard />
        </Card>
      );
    }

    return (
      <section
        ref={ref as React.Ref<HTMLElement>}
        className={cn('w-full', outerSpacingClass[density], className)}
        aria-labelledby={headingText ? titleId : undefined}
        {...props}
      >
        {(headingText || description || effectiveHeaderAction) && (
          <div className={cn('flex items-start gap-4', effectiveHeaderAction && 'justify-between')}>
            <div className="space-y-1">
              {headingText && (
                <h3 id={titleId} className="text-sm font-semibold text-foreground">
                  {number != null && <span className="text-primary">{number}.&nbsp;</span>}
                  {title}
                </h3>
              )}
              {description && <p className="text-sm text-muted-foreground">{description}</p>}
            </div>
            {effectiveHeaderAction && <div className="shrink-0">{effectiveHeaderAction}</div>}
          </div>
        )}
        <div className={gridCls}>{children}</div>
        <SectionFooter {...footerProps} inCard={false} />
      </section>
    );
  },
);
FormSection.displayName = 'FormSection';

export { FormSection };
